/* client/src/components/ui/soccer-pitch.tsx
 * ------------------------------------------------------------------
 *  6-row 3-column pitch with the exact banding requested.
 * ------------------------------------------------------------------ */
// Note: React import removed as it's not needed with modern JSX transform
import clsx from "clsx";

interface Props {
  positions: string[];
  specificPositions?: string[] | null;
  className?: string;
}

/* ───────────────── dot coordinates ───────────────── */
const dots = [
  /* id , row , col */
  { id: 1,  r: 1, c: 2 },            // Striker

  { id: 2,  r: 2, c: 1 },            // LW
  { id: 3,  r: 2, c: 2 },            // AM / CAM
  { id: 4,  r: 2, c: 3 },            // RW

  { id: 5,  r: 3, c: 1 },            // LM
  { id: 6,  r: 3, c: 2 },            // CM
  { id: 7,  r: 3, c: 3 },            // RM

  { id: 8,  r: 4, c: 1 },            // LWB
  { id: 9,  r: 4, c: 2 },            // #6 / DM
  { id: 10, r: 4, c: 3 },            // RWB

  { id: 11, r: 5, c: 1 },            // LB
  { id: 12, r: 5, c: 2 },            // CB
  { id: 13, r: 5, c: 3 },            // RB

  { id: 14, r: 6, c: 2 },            // GK
];

/* ─────────── string → dot-id map (one-to-many) ─────────── */
const map: Record<string, number[]> = {
  /* forwards */
  Striker: [1], Forward: [1],

  /* AM & wingers */
  "Attacking Midfielder": [3], "Center Forward": [1],
  "Left Winger": [2], "Right Winger": [4], "Winger": [2, 4],

  /* wide & centre mids */
  "Left Midfielder": [5], "Center Midfielder": [6], "Right Midfielder": [7],

  /* DM + wing-backs */
  "Defensive Midfielder": [9],
  "Left Wingback": [8], "Right Wingback": [10],

  /* full-backs & CB */
  "Left Back": [11], "Center Back": [12], "Right Back": [13],

  /* generic buckets (fallbacks) */
  Goalkeeper: [14],
  Defender:   [11,12,13],
  Midfielder: [6],
};

/* ──────────────────────────────────────────────────── */
export function SoccerPitch({
  positions,
  specificPositions,
  className,
}: Props) {
  const active = new Set<number>();
  const hasSpecific = specificPositions && specificPositions.length > 0;

  /* 1 ▸ always add the truly-specific roles, if present */
  (specificPositions || []).forEach((p) =>
    (map[p] || []).forEach((id) => active.add(id))
  );

  /* 2 ▸ add generic positions *only if* they won't over-highlight   */
  (positions || []).forEach((p) => {
    const isBroad =
      p === "Defender" || p === "Midfielder" || p === "Forward";
    if (hasSpecific && isBroad) return; // skip catch-alls
    (map[p] || []).forEach((id) => active.add(id));
  });

  /* 3 ▸ neutral fallback – centre-mid dot – if nothing highlighted */
  if (active.size === 0 && (positions?.length || specificPositions?.length))
    active.add(6);

  return (
    <div className={clsx("relative w-full h-full", className)}>
      {/* pitch canvas */}
      <div className="absolute inset-0 bg-green-700 border border-white rounded-sm overflow-hidden">
        {/* outer & mid lines */}
        <div className="absolute inset-[5px] border border-white rounded-sm" />
        <div className="absolute top-1/2 left-0 right-0 h-px bg-white" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 border border-white rounded-full" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-white rounded-full" />

        {/* penalty & 6-yard boxes */}
        <div className="absolute top-[5px]    left-1/2 -translate-x-1/2 w-32 h-20 border border-white" />
        <div className="absolute bottom-[5px] left-1/2 -translate-x-1/2 w-32 h-20 border border-white" />
        <div className="absolute top-[5px]    left-1/2 -translate-x-1/2 w-16 h-6  border border-white" />
        <div className="absolute bottom-[5px] left-1/2 -translate-x-1/2 w-16 h-6  border border-white" />

        {/* 6×3 grid for the dots */}
        <div className="absolute inset-0 grid grid-rows-6 grid-cols-3">
          {dots.map(({ id, r, c }) => (
            <div
              key={id}
              style={{ gridRow: r, gridColumn: c }}
              className="flex items-center justify-center"
            >
              <div
                className={clsx(
                  "w-7 h-7 rounded-full flex items-center justify-center",
                  active.has(id)
                    ? "bg-green-900 border-2 border-white"
                    : "bg-green-800 opacity-50"
                )}
              >
                {active.has(id) && (
                  <div className="w-4 h-4 bg-white rounded-full flex items-center justify-center">
                    <svg
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="w-3 h-3 text-green-700"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
export default SoccerPitch;